import React, { useState } from 'react';
import { tournamentService } from '../services/tournamentService';
import ImageUploader from './ImageUploader';

const PacketCreator = ({ onSave }) => {
  const [step, setStep] = useState(1); // 1: metadata, 2: questions
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Metadata
  const [metadata, setMetadata] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0], // Today's date
    authors: []
  });

  // Current author input
  const [authorInput, setAuthorInput] = useState('');
  const [questionCount, setQuestionCount] = useState('');

  // Questions
  const [questions, setQuestions] = useState([]);
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  // Validation
  const isMetadataValid = () => {
    return metadata.title.trim() !== '' && metadata.authors.length > 0;
  };

  const isQuestionValid = (question) => {
    return question.text.trim() !== '' && question.answer.trim() !== '';
  };

  // Metadata handlers
  const handleAddAuthor = () => {
    if (authorInput.trim() && questionCount.trim()) {
      const newAuthor = `${authorInput.trim()} (${questionCount.trim()})`;
      setMetadata({
        ...metadata,
        authors: [...metadata.authors, newAuthor]
      });
      setAuthorInput('');
      setQuestionCount('');
    }
  };

  const handleRemoveAuthor = (index) => {
    setMetadata({
      ...metadata,
      authors: metadata.authors.filter((_, i) => i !== index)
    });
  };

  // Question handlers
  const handleAddQuestion = () => {
    const newQuestion = {
      id: questions.length + 1,
      text: '',
      answer: '',
      author: metadata.authors[0]?.split(' (')[0] || 'Unknown',
      comment: '',
      source: '',
      url: '',
      image: null
    };
    setQuestions([...questions, newQuestion]);
    setExpandedQuestion(questions.length); // Auto-expand new question
  };

  const handleUpdateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const handleDeleteQuestion = (index) => {
    if (window.confirm('დარწმუნებული ხართ, რომ გსურთ ამ კითხვის წაშლა?')) {
      const updated = questions.filter((_, i) => i !== index);
      // Re-number questions
      const renumbered = updated.map((q, i) => ({ ...q, id: i + 1 }));
      setQuestions(renumbered);
      setExpandedQuestion(null);
    }
  };

  // Save to database
  const handleSavePacket = async () => {
    // Validate
    if (questions.length === 0) {
      setError('დაამატეთ მინიმუმ ერთი კითხვა');
      return;
    }

    const invalidQuestions = questions.filter(q => !isQuestionValid(q));
    if (invalidQuestions.length > 0) {
      setError(`${invalidQuestions.length} კითხვა არასრულია (ტექსტი ან პასუხი ცარიელია)`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const packet = {
        id: Date.now(),
        title: metadata.title,
        difficulty: 'Mixed',
        questions: questions.length,
        author: metadata.authors,
        date: metadata.date,
        questions_data: questions,
        created_at: new Date().toISOString()
      };

      await tournamentService.saveTournament(packet);
      
      console.log('✓ Packet saved successfully!');
      
      if (onSave) {
        onSave();
      }

    } catch (err) {
      console.error('Save error:', err);
      setError('შენახვა ვერ მოხერხდა: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Render metadata form
  const renderMetadataForm = () => (
    <div style={styles.metadataContainer}>
      <h2>პაკეტის მონაცემები</h2>
      <p style={styles.subtitle}>შეიყვანეთ ტურნირის ძირითადი ინფორმაცია</p>

      {/* Title */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          პაკეტის სახელი <span style={styles.required}>*</span>
        </label>
        <input
          style={{
            ...styles.input,
            ...(metadata.title.trim() === '' ? styles.invalidInput : {})
          }}
          type="text"
          value={metadata.title}
          onChange={(e) => setMetadata({ ...metadata, title: e.target.value })}
          placeholder="მაგ: საავტორო ტურნირი 'თბილისი 2025'"
        />
      </div>

      {/* Date */}
      <div style={styles.formGroup}>
        <label style={styles.label}>თარიღი</label>
        <input
          style={styles.input}
          type="date"
          value={metadata.date}
          onChange={(e) => setMetadata({ ...metadata, date: e.target.value })}
        />
      </div>

      {/* Authors */}
      <div style={styles.formGroup}>
        <label style={styles.label}>
          ავტორები <span style={styles.required}>*</span>
        </label>
        
        <div style={styles.authorInputContainer}>
          <input
            style={{...styles.input, flex: 2}}
            type="text"
            value={authorInput}
            onChange={(e) => setAuthorInput(e.target.value)}
            placeholder="ავტორის სახელი"
            onKeyPress={(e) => e.key === 'Enter' && handleAddAuthor()}
          />
          <input
            style={{...styles.input, flex: 1}}
            type="number"
            value={questionCount}
            onChange={(e) => setQuestionCount(e.target.value)}
            placeholder="კითხვები"
            onKeyPress={(e) => e.key === 'Enter' && handleAddAuthor()}
          />
          <button
            style={styles.addAuthorButton}
            onClick={handleAddAuthor}
            type="button"
          >
            ➕
          </button>
        </div>

        {metadata.authors.length > 0 && (
          <div style={styles.authorsList}>
            {metadata.authors.map((author, index) => (
              <div key={index} style={styles.authorTag}>
                {author}
                <button
                  style={styles.removeAuthorButton}
                  onClick={() => handleRemoveAuthor(index)}
                  type="button"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Next button */}
      <div style={styles.buttonContainer}>
        <button
          style={{
            ...styles.nextButton,
            ...(isMetadataValid() ? {} : styles.nextButtonDisabled)
          }}
          onClick={() => setStep(2)}
          disabled={!isMetadataValid()}
        >
          შემდეგი: კითხვების დამატება →
        </button>
        {!isMetadataValid() && (
          <p style={styles.validationHint}>
            შეავსეთ სახელი და დაამატეთ მინიმუმ ერთი ავტორი
          </p>
        )}
      </div>
    </div>
  );

  // Render questions form
  const renderQuestionsForm = () => (
    <div style={styles.questionsContainer}>
      <div style={styles.questionsHeader}>
        <div>
          <h2>კითხვები</h2>
          <p style={styles.subtitle}>დაამატეთ კითხვები ერთ-ერთი</p>
        </div>
        <div style={styles.questionsStats}>
          <div style={styles.statBadge}>
            სულ: {questions.length}
          </div>
          <div style={{...styles.statBadge, background: '#f44336'}}>
            არასრული: {questions.filter(q => !isQuestionValid(q)).length}
          </div>
        </div>
      </div>

      {/* Packet info recap */}
      <div style={styles.packetInfo}>
        <strong>პაკეტი:</strong> {metadata.title} • {metadata.date} • 
        ავტორები: {metadata.authors.join(', ')}
        <button
          style={styles.editMetadataButton}
          onClick={() => setStep(1)}
        >
          ✏️ რედაქტირება
        </button>
      </div>

      {/* Questions list */}
      {questions.length === 0 ? (
        <div style={styles.emptyQuestions}>
          <div style={styles.emptyIcon}>📝</div>
          <p>ჯერ არ დაგიმატებიათ კითხვები</p>
          <p style={styles.emptyHint}>დააჭირეთ ქვემოთ "➕ კითხვის დამატება" ღილაკს</p>
        </div>
      ) : (
        <div style={styles.questionsList}>
          {questions.map((question, index) => (
            <div
              key={index}
              style={{
                ...styles.questionCard,
                ...(isQuestionValid(question) ? {} : styles.invalidCard)
              }}
            >
              {/* Question header */}
              <div style={styles.questionHeader}>
                <div style={styles.questionHeaderLeft}>
                  <strong>კითხვა {question.id}</strong>
                  {!isQuestionValid(question) && (
                    <span style={styles.invalidBadge}>⚠️ არასრული</span>
                  )}
                </div>
                <div style={styles.questionHeaderRight}>
                  <button
                    style={styles.expandButton}
                    onClick={() => setExpandedQuestion(expandedQuestion === index ? null : index)}
                  >
                    {expandedQuestion === index ? '▼' : '▶'}
                  </button>
                  <button
                    style={styles.deleteButton}
                    onClick={() => handleDeleteQuestion(index)}
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Collapsed preview */}
              {expandedQuestion !== index && (
                <div style={styles.collapsedPreview}>
                  <div>
                    <strong>ტექსტი:</strong>{' '}
                    {question.text.substring(0, 60) || <em style={{color: '#c33'}}>ცარიელი</em>}
                    {question.text.length > 60 && '...'}
                  </div>
                  <div>
                    <strong>პასუხი:</strong>{' '}
                    {question.answer || <em style={{color: '#c33'}}>ცარიელი</em>}
                  </div>
                </div>
              )}

              {/* Expanded form */}
              {expandedQuestion === index && (
                <div style={styles.questionForm}>
                  {/* Question Text */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      კითხვის ტექსტი <span style={styles.required}>*</span>
                    </label>
                    <textarea
                      style={{
                        ...styles.textarea,
                        ...(question.text.trim() === '' ? styles.invalidInput : {})
                      }}
                      value={question.text}
                      onChange={(e) => handleUpdateQuestion(index, 'text', e.target.value)}
                      rows={4}
                      placeholder="შეიყვანეთ კითხვის ტექსტი..."
                    />
                  </div>

                  {/* Answer */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      პასუხი <span style={styles.required}>*</span>
                    </label>
                    <input
                      style={{
                        ...styles.input,
                        ...(question.answer.trim() === '' ? styles.invalidInput : {})
                      }}
                      type="text"
                      value={question.answer}
                      onChange={(e) => handleUpdateQuestion(index, 'answer', e.target.value)}
                      placeholder="შეიყვანეთ პასუხი..."
                    />
                  </div>

                  {/* Author */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>ავტორი</label>
                    <input
                      style={styles.input}
                      type="text"
                      value={question.author}
                      onChange={(e) => handleUpdateQuestion(index, 'author', e.target.value)}
                      placeholder="ავტორის სახელი..."
                    />
                  </div>

                  {/* Comment */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>კომენტარი</label>
                    <textarea
                      style={styles.textarea}
                      value={question.comment}
                      onChange={(e) => handleUpdateQuestion(index, 'comment', e.target.value)}
                      rows={3}
                      placeholder="კომენტარი (არასავალდებულო)..."
                    />
                  </div>

                  {/* Source */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>წყარო</label>
                    <input
                      style={styles.input}
                      type="text"
                      value={question.source}
                      onChange={(e) => handleUpdateQuestion(index, 'source', e.target.value)}
                      placeholder="წყაროს აღწერა..."
                    />
                  </div>

                  {/* URL */}
                  <div style={styles.formGroup}>
                    <label style={styles.label}>URL</label>
                    <input
                      style={styles.input}
                      type="url"
                      value={question.url}
                      onChange={(e) => handleUpdateQuestion(index, 'url', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>

                  {/* Image Upload */}
                  <ImageUploader
                    currentImageUrl={question.image}
                    packetId={`packet_${Date.now()}`}
                    questionNumber={question.id}
                    onImageUploaded={(url) => handleUpdateQuestion(index, 'image', url)}
                    onImageRemoved={() => handleUpdateQuestion(index, 'image', null)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add question button */}
      <button style={styles.addQuestionButton} onClick={handleAddQuestion}>
        ➕ კითხვის დამატება
      </button>

      {/* Error message */}
      {error && (
        <div style={styles.errorMessage}>
          ⚠️ {error}
        </div>
      )}

      {/* Save buttons */}
      <div style={styles.saveButtons}>
        <button
          style={styles.backButton}
          onClick={() => setStep(1)}
          disabled={saving}
        >
          ← უკან მონაცემებზე
        </button>
        <button
          style={{
            ...styles.saveButton,
            ...(saving || questions.length === 0 ? styles.saveButtonDisabled : {})
          }}
          onClick={handleSavePacket}
          disabled={saving || questions.length === 0}
        >
          {saving ? 'ინახება...' : '💾 შენახვა მონაცემთა ბაზაში'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.progressBar}>
        <div style={{...styles.progressStep, ...(step >= 1 ? styles.progressStepActive : {})}}>
          1. მონაცემები
        </div>
        <div style={styles.progressLine} />
        <div style={{...styles.progressStep, ...(step >= 2 ? styles.progressStepActive : {})}}>
          2. კითხვები
        </div>
      </div>

      {step === 1 ? renderMetadataForm() : renderQuestionsForm()}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '20px',
  },
  
  // Progress bar
  progressBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '40px',
  },
  progressStep: {
    padding: '10px 20px',
    background: '#e9ecef',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#adb5bd',
  },
  progressStepActive: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  progressLine: {
    width: '100px',
    height: '2px',
    background: '#e9ecef',
    margin: '0 10px',
  },

  subtitle: {
    color: '#666',
    marginTop: '5px',
    marginBottom: '30px',
  },

  // Form elements
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    fontSize: '14px',
  },
  required: {
    color: '#f44336',
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  invalidInput: {
    borderColor: '#f44336',
    background: '#fff5f5',
  },

  // Authors
  authorInputContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px',
  },
  addAuthorButton: {
    padding: '10px 20px',
    background: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  authorsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: '10px',
  },
  authorTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: '#e3f2fd',
    borderRadius: '20px',
    fontSize: '14px',
  },
  removeAuthorButton: {
    background: 'none',
    border: 'none',
    color: '#f44336',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '0',
  },

  // Buttons
  buttonContainer: {
    marginTop: '30px',
    textAlign: 'center',
  },
  nextButton: {
    padding: '15px 40px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  nextButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  validationHint: {
    marginTop: '10px',
    color: '#f44336',
    fontSize: '14px',
  },

  // Questions
  metadataContainer: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
  },
  questionsContainer: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
  },
  questionsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  questionsStats: {
    display: 'flex',
    gap: '10px',
  },
  statBadge: {
    padding: '8px 16px',
    background: '#667eea',
    color: 'white',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  packetInfo: {
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  editMetadataButton: {
    marginLeft: 'auto',
    padding: '5px 10px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  emptyQuestions: {
    textAlign: 'center',
    padding: '60px 20px',
    background: '#f8f9fa',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  emptyIcon: {
    fontSize: '4em',
    marginBottom: '15px',
  },
  emptyHint: {
    fontSize: '14px',
    color: '#999',
    marginTop: '10px',
  },
  questionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    marginBottom: '20px',
  },
  questionCard: {
    background: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: '15px',
  },
  invalidCard: {
    borderColor: '#f44336',
    background: '#fff5f5',
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  questionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  questionHeaderRight: {
    display: 'flex',
    gap: '10px',
  },
  expandButton: {
    background: '#667eea',
    color: 'white',
    border: 'none',
    padding: '5px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  deleteButton: {
    background: '#f44336',
    color: 'white',
    border: 'none',
    padding: '5px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  invalidBadge: {
    background: '#f44336',
    color: 'white',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  collapsedPreview: {
    padding: '10px',
    background: '#f9f9f9',
    borderRadius: '4px',
  },
  questionForm: {
    paddingTop: '15px',
  },
  addQuestionButton: {
    width: '100%',
    padding: '15px',
    background: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  errorMessage: {
    padding: '15px',
    background: '#fff5f5',
    border: '2px solid #f44336',
    borderRadius: '8px',
    color: '#c53030',
    marginBottom: '20px',
  },
  saveButtons: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: '15px 30px',
    background: '#e9ecef',
    color: '#495057',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '15px 40px',
    background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  saveButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

export default PacketCreator;